import React from 'react';

import styles from './contestShowPageStyles.scss';

import ContestDescription from '@buyside/components/presentation/ContestShowPage/ContestDescription/contestDescription';
import ContestJudges from '@buyside/components/presentation/ContestShowPage/ContestJudges/contestJudges';
import ContestPrizes from '@buyside/components/presentation/ContestShowPage/ContestPrizes/contestPrizes';
import ContestSponsors from '@buyside/components/presentation/ContestShowPage/ContestPrizes/contestSponsors';
import GenericErrorMessage from '@buyside/components/presentation/ErrorMessage/genericErrorMessage';
import IdeaIndexPage from '@buyside/components/presentation/IdeaIndexPage/ideaIndexPage';
import LoadingSpinner from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ResearchItem, {
  ResearchTypes
} from '@buyside/components/presentation/ResearchItem/researchItem';

import HandshakeIcon from '@buyside/svgs/handshake.svg';
import TrophyIcon from '@buyside/svgs/trophy.svg';

import {
  useContestShowQuery,
  useMainNavigationCurrentUserQuery
} from '@interfaces/graphql-typings';

const ContestShowPage: React.FC<{ contestId: string; isPublic: boolean }> = ({
  contestId,
  isPublic
}) => {
  const { data, loading, error } = useContestShowQuery({ variables: { contestId } });
  const { data: userData } = useMainNavigationCurrentUserQuery({ skip: isPublic });

  if (loading) {
    return (
      <div className={styles.fullPage}>
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (error || !data?.contest) return <GenericErrorMessage />;

  const { prizes, judges, sponsors, isCurrentUserJudge, awardWinners } = data?.contest;

  return (
    <>
      <ContestDescription
        contest={data.contest}
        canWriteIdeas={Boolean(userData?.currentUser?.canWriteIdeas)}
      />
      {judges?.length ? <ContestJudges judges={judges} /> : null}
      {prizes?.length ? (
        <>
          <h3 className={styles.prizeDescriptionHeader}>
            <TrophyIcon className={styles.icon} /> Prizes
          </h3>
          <div className={styles.prizeDescriptionBox}>
            <ContestPrizes prizes={prizes} />
          </div>
        </>
      ) : null}
      {sponsors?.length ? (
        <>
          <h3 className={styles.prizeDescriptionHeader}>
            <HandshakeIcon className={styles.icon} /> Sponsors
          </h3>
          <div className={styles.prizeDescriptionBox}>
            <ContestSponsors sponsors={sponsors} />
          </div>
        </>
      ) : null}
      {awardWinners.length ? (
        <>
          <h3 className={styles.prizeDescriptionHeader}>
            <TrophyIcon className={styles.icon} /> Winners
          </h3>
          {awardWinners.map(idea => (
            <ResearchItem
              key={idea.id}
              {...idea}
              researchType={ResearchTypes.IDEA}
              showUserDetails={
                userData?.currentUser?.canViewUserDetails ||
                userData?.currentUser?.id === idea.user?.id ||
                idea.isNonBuyside
              }
            />
          ))}
        </>
      ) : null}
      <div className={styles.ideasSection}>
        <IdeaIndexPage
          isPublic={isPublic}
          contestId={contestId}
          hideAccessBanner={isCurrentUserJudge}
        />
      </div>
    </>
  );
};

export default ContestShowPage;
